import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  GridColDef,
  GridPaginationModel,
  GridRowId,
} from '@mui/x-data-grid';
import { useTheme, useMediaQuery, Box, TextField, Button, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { downloadExcel } from '../../../util/downloadExcel';
import { Table } from '../../molecules';
import BaseFilterTableWithAccordion from '../../atoms/BaseFilterTableWithAccordion';
import { useSetSnackBar } from '../../../store/Snackbar.state';
import {
  useGetMerchantBlackListReasonHistory,
  MerchantBlackListReasonHistoryItem,
  GetMerchantBlackListReasonHistoryRequest,
  GetMerchantBlackListReasonHistoryResponse,
} from '../../../hooks/merchant';

type MerchantBlacklistHistoryTableProps = {
  merchantId?: string | number;
  onRowClick?: (data: { id: GridRowId; row: MerchantBlackListReasonHistoryItem }) => void;
};

export const MerchantBlacklistHistoryTable = ({
  merchantId,
  onRowClick,
}: MerchantBlacklistHistoryTableProps) => {
  const [tableData, setTableData] = useState<GetMerchantBlackListReasonHistoryResponse>();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [queryOptions, setQueryOptions] = useState({});
  const [filters, setFilters] = useState({
    merchantId: merchantId?.toString() || '',
    merchantName: '',
  });
  const [isColumnFilter, setIsColumnFilter] = useState(false);
  const previousQueryParams = useRef({});
  
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const setSnackbar = useSetSnackBar();
  const { mutate: getMerchantBlackListReasonHistory, isLoading } = useGetMerchantBlackListReasonHistory();

  // Filter handling - copied from MerchantListingTable
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const getQueryParams = (filters: any) => {
    const queryParams: any = {};
    for (let key in filters) {
      if (filters[key]?.toString()?.length >= 1) {
        queryParams[key] = filters[key];
      }
    }
    return queryParams;
  };

  // Handle form field changes (for manual search inputs)
  const handleFormFieldChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    // No auto API call - only manual search
  };

  // Handle column-level filter changes (from DataGrid)
  const handleFilterChange = debounce((filters: any) => {
    if (filters.clearFilter) {
      localStorage.removeItem('merchantBlacklistHistoryTableFilter');
      setIsColumnFilter(false);
      return setQueryOptions({});
    }

    const queryParams = getQueryParams(filters);
    const allValuesAtLeastThreeChars = Object.values(queryParams).every((value) => (value?.toString()?.length || 0) >= 3);
    const atLeastOneValueThreeChars = Object.values(queryParams).some((value) => (value?.toString()?.length || 0) >= 3);

    const previousKeysLength = Object.keys(previousQueryParams.current).length;
    const currentKeysLength = Object.keys(queryParams).length;

    // Eğer queryParams bir önceki ile aynıysa işlem yapma
    if (JSON.stringify(previousQueryParams.current) === JSON.stringify(queryParams)) {
      return;
    }

    if ((allValuesAtLeastThreeChars || (atLeastOneValueThreeChars && previousKeysLength > currentKeysLength))) {
      setIsColumnFilter(true); // Mark that this is from column filter
      setQueryOptions(queryParams);
      localStorage.setItem('merchantBlacklistHistoryTableFilter', JSON.stringify(queryParams));
    }

    // Son olarak, mevcut queryParams değerini bir önceki değer olarak set edin
    previousQueryParams.current = queryParams;
  }, 500);

  const hasActiveFilters = Object?.keys(queryOptions)?.length > 0;

  // Helper function to create filters for API requests
  const createApiFilters = (isFromColumnFilter: boolean = false) => {
    const filters: Array<{
      field?: string;
      values?: Array<string>;
      valueType?: number;
      operationType?: number;
    }> = [];

    // Apply current filters from queryOptions
    const queryParams = getQueryParams(queryOptions);
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value && value.toString().length >= 3) {
        let filterValue = value.toString();
        
        // For merchantStatusType, convert to uppercase
        if (key === 'merchantStatusType') {
          filterValue = value.toString().toUpperCase();
        }
        
        // For merchantId: always use valueType=2, operationType=6 (both from search button and column filter)
        // For other columns from column filter: use valueType=1, operationType=1
        if (key === 'merchantId') {
          filters.push({
            field: key,
            values: [filterValue],
            valueType: 2,
            operationType: 6,
          });
        } else if (isFromColumnFilter) {
          // Other columns from DataGrid column filter
          filters.push({
            field: key,
            values: [filterValue],
            valueType: 1,
            operationType: 1,
          });
        } else {
          // Other columns from search button
          filters.push({
            field: key,
            values: [filterValue],
            valueType: 2,
            operationType: 6,
          });
        }
      }
    });

    // If merchantId prop is provided but no other filters are active, add it as initial filter
    if (merchantId && Object.keys(queryParams).length === 0) {
      filters.push({
        field: 'merchantId',
        values: [merchantId.toString()],
        valueType: 2,
        operationType: 6,
      });
    }

    return filters;
  };

  // Excel export function for Table component
  const onSave = () => {
    const filters = createApiFilters(false);

    const requestPayload: GetMerchantBlackListReasonHistoryRequest = {
      page: 0,
      size: -1, // Get all data
      orderByDesc: true,
      orderBy: 'CreateDate',
      status: undefined,
      filters: filters,
    };

    getMerchantBlackListReasonHistory(requestPayload, {
      onSuccess: (data) => {
        if (data?.data?.result && data.data.result.length > 0) {
          const excelData = data.data.result.map((item: any) => {
            let durumTipi = item.merchantStatusType || '';
            if (durumTipi.toUpperCase() === 'BLACKLISTED') durumTipi = 'Kara Liste';
            else if (durumTipi.toUpperCase() === 'ACTIVE') durumTipi = 'Aktif';
            
            return {
              'İşyeri ID': item.merchantId,
              'İşyeri Adı': item.merchantName,
              'Kara Liste Sebebi': item.blackListReason,
              'İşyeri Durum Tipi': durumTipi,
              'Oluşturma Tarihi': item.createDate ? dayjs(item.createDate).format('DD.MM.YYYY HH:mm') : '',
              'Oluşturan Kullanıcı': item.createUserName,
            };
          });
          
          downloadExcel(excelData, 'Kara_Liste_Gecmisi');
          
          setSnackbar({
            description: `${data.data.result.length} kayıt Excel'e aktarıldı`,
            isOpen: true,
            severity: 'success',
          });
        } else {
          setSnackbar({
            description: 'İndirilecek veri bulunamadı',
            isOpen: true,
            severity: 'info',
          });
        }
      },
      onError: (error) => {
        setSnackbar({
          description: 'Excel indirme sırasında hata oluştu',
          isOpen: true,
          severity: 'error',
        });
      },
    });
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'merchantId',
      headerName: 'İşyeri ID',
      width: 150,
      sortable: true,
      filterable: true,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return params.value.toString();
      },
    },
    {
      field: 'merchantName',
      headerName: 'İşyeri Adı',
      width: 250,
      sortable: true,
      filterable: true,
    },
    {
      field: 'blackListReason',
      headerName: 'Kara Liste Sebebi',
      width: 300,
      sortable: true,
      filterable: true,
    },
    {
      field: 'merchantStatusType',
      headerName: 'İşyeri Durum Tipi',
      width: 180,
      sortable: true,
      filterable: true,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const value = params.value.toString().toUpperCase();
        if (value === 'BLACKLISTED') return 'Kara Liste';
        if (value === 'ACTIVE') return 'Aktif';
        return params.value;
      },
    },
    {
      field: 'createDate',
      headerName: 'Oluşturma Tarihi',
      width: 180,
      sortable: true,
      filterable: true,
      type: 'dateTime',
      valueGetter: (params) => {
        if (!params.value) return null;
        return new Date(params.value);
      },
      valueFormatter: (params) => {
        if (!params.value) return '';
        return dayjs(params.value).format('DD.MM.YYYY HH:mm');
      },
    },
    {
      field: 'createUserName',
      headerName: 'Oluşturan Kullanıcı',
      width: 200,
      sortable: true,
      filterable: true,
    },
  ], []);

  useEffect(() => {
    if (merchantId) {
      setFilters(prev => ({
        ...prev,
        merchantId: merchantId.toString()
      }));
    }
  }, [merchantId]);



  useEffect(() => {
    const savedFilter = localStorage.getItem("merchantBlacklistHistoryTableFilter");
    if (savedFilter) {
      setQueryOptions(JSON.parse(savedFilter));
    }
  }, []);

  useEffect(() => {
    // Use isColumnFilter state to determine filter type
    const apiFilters = createApiFilters(isColumnFilter);

    const requestPayload: GetMerchantBlackListReasonHistoryRequest = {
      size: paginationModel.pageSize,
      page: paginationModel.page,
      orderBy: "CreateDate",
      orderByDesc: true,
      status: undefined,
      filters: apiFilters,
    };

    getMerchantBlackListReasonHistory(requestPayload, {
      onSuccess: (data) => {
        setTableData(data);
        
        const itemCount = data?.data?.totalItems || 0;
        const resultCount = data?.data?.result?.length || 0;
        
        setSnackbar({
          description: `${itemCount} kayıt bulundu (${resultCount} gösteriliyor)`,
          isOpen: true,
          severity: itemCount > 0 ? 'success' : 'info',
        });
      },
      onError: (error) => {
        setSnackbar({
          description: `API Hatası: ${error?.message || 'Veriler yüklenirken bir hata oluştu'}`,
          isOpen: true,
          severity: 'error',
        });
      },
    });
  }, [
    getMerchantBlackListReasonHistory,
    paginationModel.page,
    paginationModel.pageSize,
    setSnackbar,
    queryOptions,
    shouldRefresh,
    merchantId,
    isColumnFilter,
  ]);

  const handleChangePagination = (model: GridPaginationModel) => {
    setPaginationModel(model);
    setTableData(undefined);
  };

  const handleSearch = () => {
    // Apply form filters when search button is clicked
    const queryParams = getQueryParams(filters);
    setIsColumnFilter(false); // Mark that this is from search button
    setQueryOptions(queryParams);
    if (Object.keys(queryParams).length > 0) {
      localStorage.setItem('merchantBlacklistHistoryTableFilter', JSON.stringify(queryParams));
    } else {
      localStorage.removeItem('merchantBlacklistHistoryTableFilter');
    }
    
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setShouldRefresh(prev => !prev);
  };

  const handleClear = () => {
    setFilters({
      merchantId: merchantId?.toString() || '',
      merchantName: '',
    });
    setIsColumnFilter(false);
    setQueryOptions({});
    localStorage.removeItem('merchantBlacklistHistoryTableFilter');
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setShouldRefresh(prev => !prev);
  };





  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* Filter Section */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="İşyeri ID"
            value={filters.merchantId}
            onChange={(e) => handleFormFieldChange('merchantId', e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isLoading}
            size="small"
          >
            ARA
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={isLoading}
            size="small"
          >
            TEMİZLE
          </Button>
        </Box>


        
        {/* Table */}
        <Table
          columns={columns}
          rows={tableData?.data?.result ?? []}
          paginationModel={paginationModel}
          onPaginationModelChange={handleChangePagination}
          rowCount={tableData?.data?.totalItems || 0}
          isLoading={isLoading}
          onSave={onSave}
          onRowClick={onRowClick}
          checkboxSelection={false}
          disableRowSelectionOnClick={true}
          paginationMode="server"
          exportFileName="Kara_Liste_Gecmisi"
          columnVisibilityConfigKey="merchantBlacklistHistoryTableColumnVisibilityKey"
          filterKey="merchantBlacklistHistoryTableFilter"
          handleFilterChange={handleFilterChange}
          hasActiveFilters={hasActiveFilters}
          filterMode="server"
          disableColumnFilter={false}
        />
      </Stack>
    </Box>
  );
};

export default MerchantBlacklistHistoryTable;
